import { forwardRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { Icon } from "@/components/common/Icon";

/**
 * 検索欄の見た目。
 * - plain: 枠なし。ペインの地に直接置く (左ペインの検索)
 * - framed: 白い角丸枠。他の操作と並べて置く (ラベル検索・置換)
 * - dialog: モーダル上端に敷く全幅の行 (クイックオープン)
 */
type SearchInputVariant = "plain" | "framed" | "dialog";

const PLAIN_FRAME_CLASS = "flex min-w-0 flex-1 items-center gap-1.5 py-0.5";

/** 枠がないぶん一段小さくして、一覧の項目より前に出ないようにする。 */
const PLAIN_FIELD_CLASS =
	"min-w-0 flex-1 border-0 bg-transparent text-caption text-fg-1 outline-none";

/**
 * 白い角丸枠。同じ見た目の入力欄 (検索・置換) はすべてこれを使う。
 * フォーカスでアクセントのリングに変わるのは framed のみ — dialog は開いた時点で
 * この欄しかフォーカス先がなく、常時リングが出たままになるため付けない。
 */
export const SEARCH_FRAME_CLASS =
	"flex min-w-0 flex-1 items-center gap-1.5 rounded-2 bg-surface-1 px-2 py-1 shadow-ring focus-within:shadow-[0_0_0_1px_var(--accent-solid)]";

/** 枠の中に置く素の入力欄。枠側が境界を描くので自前の境界線は持たない。 */
export const SEARCH_FIELD_CLASS =
	"min-w-0 flex-1 border-0 bg-transparent text-caption text-fg-1 outline-none";

const DIALOG_FRAME_CLASS =
	"flex min-w-0 items-center gap-2 bg-surface-1 px-3 py-2.5 shadow-[0_1px_0_var(--line-1)]";

const DIALOG_FIELD_CLASS = "min-w-0 flex-1 border-0 bg-transparent text-ui text-fg-1 outline-none";

const FRAME_CLASS: Record<SearchInputVariant, string> = {
	plain: PLAIN_FRAME_CLASS,
	framed: SEARCH_FRAME_CLASS,
	dialog: DIALOG_FRAME_CLASS,
};

const FIELD_CLASS: Record<SearchInputVariant, string> = {
	plain: PLAIN_FIELD_CLASS,
	framed: SEARCH_FIELD_CLASS,
	dialog: DIALOG_FIELD_CLASS,
};

const ICON_SIZE: Record<SearchInputVariant, number> = {
	plain: 14,
	framed: 12,
	dialog: 14,
};

interface SearchInputProps {
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	variant?: SearchInputVariant;
	/** 欄の右端に並べる補助操作 (大小区別トグル、ショートカット表記など)。 */
	trailing?: ReactNode;
	onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
	testId?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
	{ value, onChange, placeholder, variant = "plain", trailing, onKeyDown, testId },
	ref,
) {
	return (
		<label className={FRAME_CLASS[variant]}>
			<Icon name="search" size={ICON_SIZE[variant]} color="var(--fg-3)" />
			<input
				ref={ref}
				type="text"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				onKeyDown={onKeyDown}
				placeholder={placeholder}
				data-testid={testId}
				className={FIELD_CLASS[variant]}
			/>
			{trailing}
		</label>
	);
});
